// Encrypted, ordered offline outbox (epic E08).
// Commands are AES-GCM encrypted at rest in IndexedDB with a non-extractable key, so a copied
// browser profile does not expose patient data. Each command has an idempotency key and a
// monotonic sequence number; flushing is strictly in order and stops at the first transient error.

export interface QueuedCommand {
  key: string;
  seq: number;
  type: string;
  payload: unknown;
}

export interface SyncResult {
  key: string;
  status: 'applied' | 'duplicate' | 'rejected' | 'retry';
  result?: unknown;
  error?: string;
}

const DB_NAME = 'movox-kiosk';
const STORES = { outbox: 'outbox', dead: 'deadletter', keys: 'keys' } as const;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      db.createObjectStore(STORES.outbox, { keyPath: 'seq' });
      db.createObjectStore(STORES.dead, { keyPath: 'seq' });
      db.createObjectStore(STORES.keys);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const tx = <T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    const r = fn(db.transaction(store, mode).objectStore(store));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });

interface Sealed {
  seq: number;
  key: string;
  iv: Uint8Array;
  data: ArrayBuffer;
  queuedAt: string;
  error?: string;
}

export class Outbox {
  private dbp = openDb();
  private keyp: Promise<CryptoKey>;
  private flushing: Promise<void> | null = null;
  private listeners = new Set<(depth: number) => void>();
  private kioskId: string;
  private nextSeq: number;

  constructor(kioskId: string) {
    this.kioskId = kioskId;
    const stored = Number(localStorage.getItem(`movox.seq.${kioskId}`));
    // Sequence numbers must keep increasing across reloads; seed from the clock on first use.
    this.nextSeq = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    this.keyp = this.dbp.then(async (db) => {
      const existing = await tx<CryptoKey | undefined>(db, STORES.keys, 'readonly', (s) => s.get('outbox'));
      if (existing) return existing;
      const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
      await tx(db, STORES.keys, 'readwrite', (s) => s.put(key, 'outbox'));
      return key;
    });
  }

  onChange(fn: (depth: number) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private async notify() {
    const d = await this.depth();
    for (const l of this.listeners) l(d);
  }

  async enqueue(type: string, payload: unknown): Promise<QueuedCommand> {
    const seq = this.nextSeq++;
    localStorage.setItem(`movox.seq.${this.kioskId}`, String(this.nextSeq));
    const cmd: QueuedCommand = { key: `${this.kioskId}:${seq}:${crypto.randomUUID().slice(0, 8)}`, seq, type, payload };
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await this.keyp, new TextEncoder().encode(JSON.stringify(cmd)));
    const db = await this.dbp;
    await tx(db, STORES.outbox, 'readwrite', (s) => s.put({ seq, key: cmd.key, iv, data, queuedAt: new Date().toISOString() } satisfies Sealed));
    void this.notify();
    return cmd;
  }

  private async open(sealed: Sealed): Promise<QueuedCommand> {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv as Uint8Array<ArrayBuffer> }, await this.keyp, sealed.data);
    return JSON.parse(new TextDecoder().decode(plain));
  }

  async depth(): Promise<number> {
    return tx<number>(await this.dbp, STORES.outbox, 'readonly', (s) => s.count());
  }

  async deadLetters(): Promise<{ key: string; error?: string; queuedAt: string }[]> {
    const all = await tx<Sealed[]>(await this.dbp, STORES.dead, 'readonly', (s) => s.getAll());
    return all.map((d) => ({ key: d.key, error: d.error, queuedAt: d.queuedAt }));
  }

  /**
   * Sends queued commands in order. `send` posts a batch to the server and returns per-command results.
   * Resolves with all results received in this flush (keyed by idempotency key).
   */
  flush(send: (cmds: QueuedCommand[]) => Promise<SyncResult[]>): Promise<Map<string, SyncResult>> {
    const results = new Map<string, SyncResult>();
    const run = async () => {
      const db = await this.dbp;
      for (;;) {
        const sealed = await tx<Sealed[]>(db, STORES.outbox, 'readonly', (s) => s.getAll(null, 50));
        if (!sealed.length) return;
        const cmds = await Promise.all(sealed.map((s) => this.open(s)));
        const out = await send(cmds); // throws when offline: everything stays queued
        let stop = false;
        for (const r of out) {
          results.set(r.key, r);
          const s = sealed.find((x) => x.key === r.key);
          if (!s) continue;
          if (r.status === 'retry') {
            stop = true;
            break;
          }
          await tx(db, STORES.outbox, 'readwrite', (st) => st.delete(s.seq));
          if (r.status === 'rejected') await tx(db, STORES.dead, 'readwrite', (st) => st.put({ ...s, error: r.error }));
        }
        void this.notify();
        if (stop) return;
      }
    };
    // Serialise flushes; a caller arriving mid-flush waits and then runs its own pass.
    const next = (this.flushing ?? Promise.resolve()).catch(() => {}).then(run);
    this.flushing = next;
    next.catch(() => {}).finally(() => {
      if (this.flushing === next) this.flushing = null;
    });
    return next.then(() => results);
  }
}
